import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table, Button, Modal, Form, Input, Switch, InputNumber,
  Tag, Space, Popconfirm, Tooltip, Badge, ColorPicker, Select,
  Divider, Checkbox,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, HolderOutlined,
  SettingOutlined, CopyOutlined, UserAddOutlined,
} from "@ant-design/icons";
import api from "../../../core/axios/axiosInstance";
import {
  fetchAdminPlans, fetchPlansConfig, createPlan, updatePlan, deletePlan,
  togglePlanStatus, reorderPlans, updateCommonFeatures,
  updateGlobalTrialPeriod, updateEmailReminderSchedule, updateActivePeriods,
} from "../../../core/data/redux/slices/PlanSlice";
import Credits from "./Credits";





const { TextArea } = Input;
const { Option } = Select;

const ALL_PERIODS = ["monthly", "quarterly", "semiannual", "yearly"];
// The frontend billing page only shows Monthly and Yearly — these are the primary periods.
const PRIMARY_PERIODS = ["monthly", "yearly"];
const PERIOD_LABELS = { monthly: "Monthly", quarterly: "Quarterly (3mo)", semiannual: "6 Months", yearly: "Yearly (Annual)" };

const emptyPricing = { monthly: { price: 0, discountPercent: 0 }, quarterly: { price: 0, discountPercent: 10 }, semiannual: { price: 0, discountPercent: 15 }, yearly: { price: 0, discountPercent: 20 } };

// ── Draggable feature list ────────────────────────────────────────────────────
const FeatureList = ({ features, onChange }) => {
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const handleDragStart = (i) => { dragItem.current = i; };
  const handleDragEnter = (i) => { dragOverItem.current = i; };
  const handleDragEnd = () => {
    const copy = [...features];
    const dragged = copy.splice(dragItem.current, 1)[0];
    copy.splice(dragOverItem.current, 0, dragged);
    dragItem.current = null;
    dragOverItem.current = null;
    onChange(copy.map((f, i) => ({ ...f, order: i })));
  };

  const addFeature = () => onChange([...features, { text: "", description: "", order: features.length }]);
  const removeFeature = (i) => onChange(features.filter((_, idx) => idx !== i));
  const updateField = (i, field, val) => {
    const copy = [...features];
    copy[i] = { ...copy[i], [field]: val };
    onChange(copy);
  };

  return (
    <div>
      {features.map((f, i) => (
        <div
          key={i}
          draggable
          onDragStart={() => handleDragStart(i)}
          onDragEnter={() => handleDragEnter(i)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => e.preventDefault()}
          style={{ marginBottom: 8, cursor: "grab", background: "#fafafa", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <HolderOutlined style={{ color: "#9ca3af", flexShrink: 0 }} />
            <Input
              value={f.text}
              onChange={(e) => updateField(i, "text", e.target.value)}
              placeholder="Feature text"
              size="small"
              style={{ flex: 1 }}
            />
            <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => removeFeature(i)} />
          </div>
          <Input
            value={f.description || ""}
            onChange={(e) => updateField(i, "description", e.target.value)}
            placeholder="Info tooltip (optional) — shown as ⓘ icon next to feature"
            size="small"
            style={{ marginTop: 6, marginLeft: 24, width: "calc(100% - 24px)", color: "#6b7280" }}
          />
        </div>
      ))}
      <Button type="dashed" icon={<PlusOutlined />} onClick={addFeature} size="small" block>
        Add Feature
      </Button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export default function AdminPlans() {
  const dispatch = useDispatch();
  const { plans: rawPlans, loading, actionLoading, globalTrialDays, globalReminderDays, activePeriods } = useSelector((state) => state.plans);
  const plans = rawPlans || [];

  const [form] = Form.useForm();
  const [settingsForm] = Form.useForm();

  const [planModal, setPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [localFeatures, setLocalFeatures] = useState([]);
  const [localCommonFeatures, setLocalCommonFeatures] = useState([]);
  const [localPricing, setLocalPricing] = useState(emptyPricing);
  const [tagColor, setTagColor] = useState("#7c3aed");

  const [settingsModal, setSettingsModal] = useState(false);
  const [commonFeaturesModal, setCommonFeaturesModal] = useState(false);
  const [selectedPricingPeriod, setSelectedPricingPeriod] = useState("monthly");
  const [globalCommonFeatures, setGlobalCommonFeatures] = useState([]);

  const [dragPlanIndex, setDragPlanIndex] = useState(null);
  const [dragOverPlanIndex, setDragOverPlanIndex] = useState(null);

  const [duplicatingId, setDuplicatingId] = useState(null);

  // Assign plan to user
  const [forceDeletePlan, setForceDeletePlan] = useState(null); // { planId, name, activeSubs }
  const [assignModal, setAssignModal] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignSearchLoading, setAssignSearchLoading] = useState(false);
  const [assignSelectedUser, setAssignSelectedUser] = useState(null);
  const [assignPlanId, setAssignPlanId] = useState(null);
  const [assignPeriod, setAssignPeriod] = useState("monthly");
  const [assignLoading, setAssignLoading] = useState(false);

  const [trialSaving, setTrialSaving] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [periodsSaving, setPeriodsSaving] = useState(false);
  const [localActivePeriods, setLocalActivePeriods] = useState(activePeriods);

  useEffect(() => {
    dispatch(fetchAdminPlans());
  }, [dispatch]);

  // ── Plan modal open ───────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingPlan(null);
    form.resetFields();
    setLocalFeatures([]);
    setLocalCommonFeatures([]);
    setLocalPricing(emptyPricing);
    setTagColor("#7c3aed");
    setPlanModal(true);
  };

  const openEdit = (plan) => {
    const firstActivePeriod = ALL_PERIODS.find((p) => plan.pricing?.[p]?.price > 0);
    setEditingPlan(plan);
    form.setFieldsValue({
      name: plan.name,
      description: plan.description,
      tag: plan.tag,
      isActive: plan.isActive,
      isEnterprise: plan.isEnterprise || false,
      agentPrice: plan.agentPrice || 0,
    });
    setLocalFeatures(plan.features || []);
    setLocalCommonFeatures(plan.commonFeatures || []);
    setLocalPricing(plan.pricing || emptyPricing);
    setTagColor(plan.tagColor || "#7c3aed");
    if (firstActivePeriod) setSelectedPricingPeriod(firstActivePeriod);
    setPlanModal(true);
  };

  const handleSavePlan = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        tagColor,
        features: localFeatures.filter((f) => f.text.trim()),
        commonFeatures: localCommonFeatures.filter((f) => f.text.trim()),
        pricing: localPricing,
        isEnterprise: values.isEnterprise || false,
      };

      if (editingPlan) {
        await dispatch(updatePlan({ planId: editingPlan._id, ...payload })).unwrap();
      } else {
        await dispatch(createPlan(payload)).unwrap();
      }
      setPlanModal(false);
      dispatch(fetchAdminPlans());
    } catch (_) {}
  };

  // ── Drag-to-reorder plans ─────────────────────────────────────────────────
  const handlePlanDragEnd = () => {
    if (dragPlanIndex === null || dragOverPlanIndex === null) return;
    const copy = [...plans];
    const [moved] = copy.splice(dragPlanIndex, 1);
    copy.splice(dragOverPlanIndex, 0, moved);
    const orderedIds = copy.map((p) => p._id);
    dispatch(reorderPlans(orderedIds));
    setDragPlanIndex(null);
    setDragOverPlanIndex(null);
  };

  // ── Duplicate plan ────────────────────────────────────────────────────────
  const handleDuplicate = async (plan) => {
    setDuplicatingId(plan._id);
    const payload = {
      name: `Copy of ${plan.name}`,
      description: plan.description,
      tag: plan.tag,
      tagColor: plan.tagColor,
      isActive: true,
      isEnterprise: plan.isEnterprise,
      agentPrice: plan.agentPrice || 0,
      features: plan.features || [],
      commonFeatures: plan.commonFeatures || [],
      pricing: {
        monthly:    { price: plan.pricing?.monthly?.price    || 0, discountPercent: plan.pricing?.monthly?.discountPercent    || 0 },
        quarterly:  { price: plan.pricing?.quarterly?.price  || 0, discountPercent: plan.pricing?.quarterly?.discountPercent  || 0 },
        semiannual: { price: plan.pricing?.semiannual?.price || 0, discountPercent: plan.pricing?.semiannual?.discountPercent || 0 },
        yearly:     { price: plan.pricing?.yearly?.price     || 0, discountPercent: plan.pricing?.yearly?.discountPercent     || 0 },
      },
    };
    try {
      await dispatch(createPlan(payload)).unwrap();
      await dispatch(fetchAdminPlans()).unwrap();
    } catch (_) {}
    finally { setDuplicatingId(null); }
  };

  // ── Assign plan to user ───────────────────────────────────────────────────
  const handleUserSearch = async (q) => {
    setAssignSearch(q);
    if (!q.trim()) { setAssignUsers([]); return; }
    setAssignSearchLoading(true);
    try {
      const res = await api.get(`/plans/users/search?q=${encodeURIComponent(q)}`);
      setAssignUsers(res.data.users || []);
    } catch (_) {}
    finally { setAssignSearchLoading(false); }
  };

  const handleAssignPlan = async () => {
    if (!assignSelectedUser || !assignPlanId) return;
    setAssignLoading(true);
    try {
      await api.post("/plans/assign-user", {
        userId: assignSelectedUser,
        planId: assignPlanId,
        billingPeriod: assignPeriod,
      });
      setAssignModal(false);
      setAssignSelectedUser(null);
      setAssignPlanId(null);
      setAssignUsers([]);
      setAssignSearch("");
      dispatch(fetchAdminPlans());
    } catch (err) {
      console.error(err);
    } finally {
      setAssignLoading(false);
    }
  };

  // ── Pricing update helpers ────────────────────────────────────────────────
  const setPricingField = (period, field, value) => {
    setLocalPricing((prev) => ({
      ...prev,
      [period]: { ...prev[period], [field]: value },
    }));
  };

  // Both monthly and yearly can be active simultaneously.
  // Quarterly and semiannual are zeroed out since the frontend only shows monthly/yearly.
  const handlePriceChange = (period, value) => {
    const newPrice = value || 0;
    setLocalPricing((prev) => {
      const updated = { ...prev };
      updated[period] = { ...prev[period], price: newPrice };
      // Zero out quarterly/semiannual if setting monthly or yearly (they're unused by the frontend)
      if (period === "monthly" || period === "yearly") {
        updated.quarterly  = { ...prev.quarterly,  price: 0 };
        updated.semiannual = { ...prev.semiannual, price: 0 };
      }
      return updated;
    });
  };

  // ── Pause modal ───────────────────────────────────────────────────────────
  // const handlePause = async (values) => {
  //   await dispatch(pauseUserSubscription(values)).unwrap().catch(() => {});
  //   pauseForm.resetFields();
  //   setPauseModal(false);
  // };

  // ── Common features modal ─────────────────────────────────────────────────
  const openCommonFeatures = () => {
    setGlobalCommonFeatures(plans[0]?.commonFeatures || []);
    setCommonFeaturesModal(true);
  };

  const saveCommonFeatures = async () => {
    await dispatch(updateCommonFeatures(globalCommonFeatures.filter((f) => f.text.trim()))).unwrap();
    setCommonFeaturesModal(false);
    dispatch(fetchAdminPlans());
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      title: "",
      width: 40,
      render: (_, __, idx) => (
        <div
          draggable
          onDragStart={() => setDragPlanIndex(idx)}
          onDragEnter={() => setDragOverPlanIndex(idx)}
          onDragEnd={handlePlanDragEnd}
          onDragOver={(e) => e.preventDefault()}
          style={{ cursor: "grab", padding: "0 8px" }}
        >
          <HolderOutlined style={{ color: "#9ca3af" }} />
        </div>
      ),
    },
    {
      title: "Plan Name",
      dataIndex: "name",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <span style={{ fontWeight: 600 }}>{name}</span>
            {record.isEnterprise && <Tag color="geekblue">Enterprise</Tag>}
            {record.tag && <Tag color={record.tagColor || "#7c3aed"}>{record.tag}</Tag>}
          </Space>
          <span style={{ color: "#6b7280", fontSize: 12 }}>{record.description}</span>
        </Space>
      ),
    },
    {
      title: "Price",
      render: (_, r) => {
        if (r.isEnterprise) return <Tag color="geekblue">Contact Us</Tag>;
        const monthly = r.pricing?.monthly?.price || 0;
        const yearly  = r.pricing?.yearly?.price  || 0;
        if (!monthly && !yearly) return <span style={{ color: "#9ca3af" }}>—</span>;
        return (
          <Space direction="vertical" size={2}>
            {monthly > 0 && <span style={{ fontSize: 13 }}><strong>${monthly}/mo</strong> (Monthly)</span>}
            {yearly  > 0 && (
              <span style={{ fontSize: 13 }}>
                <strong>${yearly}/mo</strong> (Annual
                {r.pricing.yearly.discountPercent > 0 && <Tag color="green" style={{ fontSize: 10, marginLeft: 4 }}>-{r.pricing.yearly.discountPercent}%</Tag>})
              </span>
            )}
          </Space>
        );
      },
    },
    {
      title: "Subscribers",
      dataIndex: "subscriberCount",
      render: (v) => {
        const monthly = v?.monthly || 0;
        const yearly  = v?.yearly  || 0;
        return (
          <Space direction="vertical" size={2}>
            <span style={{ fontSize: 12 }}>
              Monthly: <Badge count={monthly} showZero style={{ backgroundColor: monthly > 0 ? "#6366f1" : "#d1d5db" }} />
            </span>
            <span style={{ fontSize: 12 }}>
              Annual: <Badge count={yearly} showZero style={{ backgroundColor: yearly > 0 ? "#10b981" : "#d1d5db" }} />
            </span>
          </Space>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (active, record) => (
        <Switch
          checked={active}
          size="small"
          onChange={() => dispatch(togglePlanStatus(record._id))}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="Duplicate">
            <Button type="text" icon={<CopyOutlined />} loading={duplicatingId === record._id} onClick={() => handleDuplicate(record)} />
          </Tooltip>
          {/* <Tooltip title="Pause User Subscription">
            <Button type="text" icon={<PauseCircleOutlined />} onClick={() => setPauseModal(true)} />
          </Tooltip> */}
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => setForceDeletePlan({ planId: record._id, name: record.name, activeSubs: (record.subscriberCount?.monthly || 0) + (record.subscriberCount?.yearly || 0) })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700 }}>Plans Management</h3>
            <span style={{ color: "#6b7280" }}>Create and manage subscription plans</span>
          </div>
          <Space>
            <Button icon={<SettingOutlined />} onClick={openCommonFeatures}>Common Features</Button>
            {/* <Button icon={<PauseCircleOutlined />} onClick={() => setPauseModal(true)}>Pause/Resume Sub</Button> */}
            <Button icon={<SettingOutlined />} onClick={async () => {
              settingsForm.setFieldsValue({
                trialDays: globalTrialDays,
                reminderDays: globalReminderDays,
              });
              setLocalActivePeriods(activePeriods);
              setSettingsModal(true);
              try {
                const config = await dispatch(fetchPlansConfig()).unwrap();
                settingsForm.setFieldsValue({
                  trialDays: config.defaultTrialDays ?? globalTrialDays,
                  reminderDays: (config.defaultEmailReminderDays?.length ? config.defaultEmailReminderDays : null)?.join(",") ?? globalReminderDays,
                });
                if (config.activePeriods?.length) setLocalActivePeriods(config.activePeriods);
              } catch {}
            }}>Trial Settings</Button>
            <Button icon={<UserAddOutlined />} onClick={() => { setAssignModal(true); setAssignSelectedUser(null); setAssignPlanId(null); setAssignUsers([]); setAssignSearch(""); }}>
              Assign Plan to User
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: "#6366f1" }}>
              Add Plan
            </Button>
          </Space>
        </div>

        {/* Plans Table */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <Table
            columns={columns}
            dataSource={plans}
            rowKey="_id"
            loading={loading}
            pagination={false}
          />
        </div>

        {/* ── Add/Edit Plan Modal ───────────────────────────────────────────── */}
        <Modal
          title={editingPlan ? "Edit Plan" : "Add New Plan"}
          open={planModal}
          onCancel={() => setPlanModal(false)}
          onOk={handleSavePlan}
          confirmLoading={actionLoading}
          width={720}
          okText="Save Plan"
          okButtonProps={{ style: { background: "#6366f1" } }}
        >
          <Form form={form} layout="vertical">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Form.Item label="Plan Name" name="name" rules={[{ required: true, message: "Plan name required" }]}>
                <Input placeholder="e.g. Professional" />
              </Form.Item>
              <Form.Item label="Tag (e.g. Most Popular)" name="tag">
                <Input placeholder="Tag label" />
              </Form.Item>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <Form.Item label="Tag Color" style={{ marginBottom: 0 }}>
                  <ColorPicker
                    value={tagColor}
                    onChange={(color) => setTagColor(color.toHexString())}
                    showText
                  />
                </Form.Item>
              </div>
              <Form.Item label="Active" name="isActive" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch />
              </Form.Item>
              <Form.Item label="Enterprise (Contact Us)" name="isEnterprise" valuePropName="checked" style={{ marginBottom: 0 }}
                tooltip="Enterprise plans show a 'Contact Us' button instead of a price and subscribe button.">
                <Switch />
              </Form.Item>
            </div>

            <Form.Item label="Description" name="description">
              <TextArea rows={2} placeholder="Brief description" />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isEnterprise !== cur.isEnterprise}>
              {({ getFieldValue }) => !getFieldValue("isEnterprise") && (
                <>
                  <Form.Item
                    label="Per-Agent Monthly Price (USD)"
                    name="agentPrice"
                    initialValue={0}
                    tooltip="Extra charge per agent/user seat per month. CompanyAdmin selects how many agents they need when subscribing."
                  >
                    <InputNumber min={0} step={1} precision={2} prefix="$" style={{ width: "100%" }} placeholder="0.00" />
                  </Form.Item>

                  <Divider>Pricing</Divider>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 8 }}>
                    {/* Monthly */}
                    <div style={{ background: "#f9fafb", borderRadius: 10, padding: "14px 16px", border: "1px solid #e5e7eb" }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: "#374151" }}>Monthly</div>
                      <label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "#6b7280" }}>Price ($/month)</label>
                      <InputNumber
                        min={0} step={0.01} prefix="$" style={{ width: "100%" }}
                        value={localPricing.monthly?.price || 0}
                        onChange={(v) => handlePriceChange("monthly", v)}
                      />
                      {localPricing.monthly?.price > 0 && (
                        <div style={{ fontSize: 11, color: "#059669", marginTop: 6 }}>
                          Billed ${localPricing.monthly.price.toFixed(2)}/month
                        </div>
                      )}
                    </div>

                    {/* Yearly */}
                    <div style={{ background: "#f0fdf4", borderRadius: 10, padding: "14px 16px", border: "1px solid #bbf7d0" }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: "#374151" }}>
                        Annual <span style={{ fontSize: 11, background: "#dcfce7", color: "#16a34a", padding: "1px 7px", borderRadius: 20, marginLeft: 4 }}>Save more</span>
                      </div>
                      <label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "#6b7280" }}>Price ($/month equivalent)</label>
                      <InputNumber
                        min={0} step={0.01} prefix="$" style={{ width: "100%" }}
                        value={localPricing.yearly?.price || 0}
                        onChange={(v) => handlePriceChange("yearly", v)}
                      />
                      <label style={{ display: "block", marginBottom: 4, marginTop: 8, fontSize: 12, color: "#6b7280" }}>Annual Discount %</label>
                      <InputNumber
                        min={0} max={100} suffix="%" style={{ width: "100%" }}
                        value={localPricing.yearly?.discountPercent || 0}
                        onChange={(v) => setPricingField("yearly", "discountPercent", v || 0)}
                      />
                      {localPricing.yearly?.price > 0 && (
                        <div style={{ fontSize: 11, color: "#059669", marginTop: 6 }}>
                          Billed ${(localPricing.yearly.price * 12 * (1 - (localPricing.yearly.discountPercent || 0) / 100)).toFixed(2)}/year
                          {localPricing.monthly?.price > 0 && (
                            <> · saves ${(localPricing.monthly.price * 12 - localPricing.yearly.price * 12 * (1 - (localPricing.yearly.discountPercent || 0) / 100)).toFixed(2)}</>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </Form.Item>

            {/* Enterprise plan notice */}
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isEnterprise !== cur.isEnterprise}>
              {({ getFieldValue }) => getFieldValue("isEnterprise") && (
                <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "12px 16px", marginBottom: 16, color: "#0369a1", fontSize: 13 }}>
                  Enterprise plans display a <strong>"Contact Us"</strong> button instead of pricing. Add features below that will be shown to visitors.
                </div>
              )}
            </Form.Item>

            <Divider>Plan Features</Divider>
            <FeatureList features={localFeatures} onChange={setLocalFeatures} />

            <Divider>Common Features (shown on this plan)</Divider>
            <FeatureList features={localCommonFeatures} onChange={setLocalCommonFeatures} />
          </Form>
        </Modal>

        {/* ── Common Features Modal ─────────────────────────────────────────── */}
        <Modal
          title="Update Common Features (applied to ALL plans)"
          open={commonFeaturesModal}
          onCancel={() => setCommonFeaturesModal(false)}
          onOk={saveCommonFeatures}
          confirmLoading={actionLoading}
          okText="Save for All Plans"
          okButtonProps={{ style: { background: "#6366f1" } }}
        >
          <FeatureList features={globalCommonFeatures} onChange={setGlobalCommonFeatures} />
        </Modal>

        {/* ── Pause / Resume Subscription Modal ────────────────────────────── */}
        {/* <Modal
          title="Manage User Subscription"
          open={pauseModal}
          onCancel={() => { setPauseModal(false); pauseForm.resetFields(); }}
          footer={null}
          width={480}
        >
          <Form form={pauseForm} layout="vertical" onFinish={handlePause}>
            <Form.Item label="User ID" name="userId" rules={[{ required: true, message: "User ID required" }]}>
              <Input placeholder="MongoDB user ID" />
            </Form.Item>
            <Form.Item label="Pause Duration (days)" name="pauseDurationDays" initialValue={30}>
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ background: "#f59e0b" }}>
                Pause Subscription
              </Button>
              <Button
                onClick={async () => {
                  const { userId } = await pauseForm.validateFields(["userId"]);
                  await dispatch(resumeUserSubscription(userId)).unwrap().catch(() => {});
                  setPauseModal(false);
                  pauseForm.resetFields();
                }}
                loading={actionLoading}
                style={{ background: "#10b981", color: "#fff", borderColor: "#10b981" }}
              >
                Resume Subscription
              </Button>
            </Space>
          </Form>
        </Modal> */}

        {/* ── Trial & Email Settings Modal ──────────────────────────────────── */}
        <Modal
          title="Trial Period & Email Reminder Settings"
          open={settingsModal}
          onCancel={() => setSettingsModal(false)}
          footer={null}
          width={520}
        >
          <Form form={settingsForm} layout="vertical">
            <Divider orientation="left">Trial Period</Divider>
            <Form.Item label="Global Trial Duration (days)" name="trialDays">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Button
              type="primary"
              style={{ background: "#6366f1", marginBottom: 24 }}
              loading={trialSaving}
              onClick={async () => {
                try {
                  const vals = await settingsForm.validateFields(["trialDays"]);
                  setTrialSaving(true);
                  await dispatch(updateGlobalTrialPeriod(vals.trialDays)).unwrap();
                  setSettingsModal(false);
                } catch (_) {} finally {
                  setTrialSaving(false);
                }
              }}
            >
              Update Global Trial
            </Button>

            <Divider orientation="left">Email Reminders</Divider>
            <p style={{ color: "#6b7280", fontSize: 13 }}>
              Send reminder emails X days before expiry. Enter comma-separated days (e.g. 7,3,1).
            </p>
            <Form.Item label="Reminder Days (global)" name="reminderDays">
              <Input placeholder="7,3,1" />
            </Form.Item>
            <Button
              type="primary"
              style={{ background: "#6366f1" }}
              loading={reminderSaving}
              onClick={async () => {
                try {
                  const vals = await settingsForm.validateFields(["reminderDays"]);
                  const raw = vals.reminderDays;
                  const days = (Array.isArray(raw) ? raw : String(raw || "").split(","))
                    .map((d) => Number(String(d).trim()))
                    .filter((d) => Number.isInteger(d) && d > 0);
                  if (days.length === 0) return;
                  setReminderSaving(true);
                  await dispatch(updateEmailReminderSchedule({ reminderDays: days, global: true })).unwrap();
                  setSettingsModal(false);
                } catch (_) {} finally {
                  setReminderSaving(false);
                }
              }}
            >
              Update Reminder Schedule
            </Button>

            <Divider orientation="left">Billing Periods</Divider>
            <p style={{ color: "#6b7280", fontSize: 13 }}>
              Select which billing periods are available to customers on the upgrade/pricing page.
            </p>
            <Checkbox.Group
              options={ALL_PERIODS.map((p) => ({ label: PERIOD_LABELS[p], value: p }))}
              value={localActivePeriods}
              onChange={setLocalActivePeriods}
              style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}
            />
            <Button
              type="primary"
              style={{ background: "#6366f1" }}
              loading={periodsSaving}
              disabled={localActivePeriods.length === 0}
              onClick={async () => {
                try {
                  setPeriodsSaving(true);
                  await dispatch(updateActivePeriods(localActivePeriods)).unwrap();
                  setSettingsModal(false);
                } catch (_) {} finally {
                  setPeriodsSaving(false);
                }
              }}
            >
              Update Billing Periods
            </Button>
          </Form>
        </Modal>
        {/* ── Assign Plan to User Modal ─────────────────────────────────────── */}
        <Modal
          title={<Space><UserAddOutlined style={{ color: "#6366f1" }} /> Assign Plan to User</Space>}
          open={assignModal}
          onCancel={() => setAssignModal(false)}
          onOk={handleAssignPlan}
          confirmLoading={assignLoading}
          okText="Assign Plan"
          okButtonProps={{ style: { background: "#6366f1" }, disabled: !assignSelectedUser || !assignPlanId }}
          width={480}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Search User (by name or email)</label>
              <Select
                showSearch
                style={{ width: "100%" }}
                placeholder="Type to search users..."
                filterOption={false}
                onSearch={handleUserSearch}
                onChange={(val) => setAssignSelectedUser(val)}
                value={assignSelectedUser}
                loading={assignSearchLoading}
                notFoundContent={assignSearch ? "No users found" : "Start typing to search"}
              >
                {assignUsers.map((u) => (
                  <Option key={u._id} value={u._id}>
                    {u.firstname} {u.lastname} — <span style={{ color: "#6b7280" }}>{u.email}</span>
                    {u.planStatus && <Tag style={{ marginLeft: 8 }} color={u.planStatus === "active" ? "green" : "orange"}>{u.planStatus}</Tag>}
                  </Option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Select Plan</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Choose a plan"
                value={assignPlanId}
                onChange={(val) => {
                  setAssignPlanId(val);
                  const plan = plans.find((p) => p._id === val);
                  const activePeriod = ALL_PERIODS.find((p) => plan?.pricing?.[p]?.price > 0);
                  if (activePeriod) setAssignPeriod(activePeriod);
                }}
              >
                {plans.filter((p) => !p.isEnterprise && p.isActive).map((p) => (
                  <Option key={p._id} value={p._id}>{p.name}</Option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Billing Period</label>
              <Select style={{ width: "100%" }} value={assignPeriod} onChange={setAssignPeriod}>
                <Option value="monthly">Monthly</Option>
                <Option value="yearly">Annually</Option>
              </Select>
            </div>
          </div>
        </Modal>

        {/* ── Delete Confirmation Modal ────────────────────────────────────── */}
        <Modal
          title="Delete Plan"
          open={!!forceDeletePlan}
          onCancel={() => setForceDeletePlan(null)}
          onOk={async () => {
            await dispatch(deletePlan({ planId: forceDeletePlan.planId, force: true }));
            setForceDeletePlan(null);
          }}
          okText="Yes, Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          confirmLoading={actionLoading}
        >
          <p>Are you sure you want to delete <strong>{forceDeletePlan?.name}</strong>?</p>
          {forceDeletePlan?.activeSubs > 0 && (
            <p style={{ color: "#f59e0b" }}>
              ⚠️ This plan has <strong>{forceDeletePlan.activeSubs}</strong> active subscriber(s).
              Their subscriptions will be cancelled immediately.
            </p>
          )}
          <p style={{ color: "#6b7280", fontSize: 13 }}>This action cannot be undone.</p>
        </Modal>
      </div>
    </div>
  );
}
